<template>
  <div>
    <HeroHeader
      v-if="!embedded"
      eyebrow="LETRAS"
      :title="'Letras — ' + (client.name || '…')"
      :counter-num="list.length"
      counter-unit="letras"
      :back="true"
      @back="$router.go(-1)"
    >
      <template #toolbar>
        <button class="btn btn--outline" @click="showGenerateSidebar = true">Generar letras</button>
        <button class="btn btn--outline" @click="showNotification = true">Notificar</button>
        <button class="btn" @click="showNewSidebar = true">+ Agregar</button>
      </template>
    </HeroHeader>

    <ChipsBar
      :chips="[{label:'Todas',value:null},{label:'Pendientes',value:false},{label:'Pagadas',value:true}]"
      :model-value="filterPaid"
      @update:modelValue="filterPaid = $event; goFilter()"
    />

    <div class="filter-bar filter-bar--no-top">
      <SortSelect :options="sortOptions" v-model="sortBy" />
    </div>

    <div class="dt-wrapper">
      <table class="dt" v-if="list.length > 0">
        <thead>
          <tr>
            <th>Monto</th>
            <th>Vencimiento</th>
            <th>Fecha de pago</th>
            <th>Estado</th>
            <th>Comprobante</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="o in displayList" :key="o.id">
            <td>
              <span class="dt-cell__primary">{{ formatAmount(o.total, o.client.currency) }}</span>
            </td>
            <td>{{ formatDate(o.deadline) }}</td>
            <td>{{ o.paymentDate ? formatDate(o.paymentDate) : '—' }}</td>
            <td>
              <span class="badge" :class="o.paid ? 'badge--success' : 'badge--error'">
                {{ o.paid ? 'Pagada' : 'Pendiente' }}
              </span>
            </td>
            <td>
              <div v-if="voucherCount(o)" class="voucher-cell">
                <VoucherStatus :audit="o.voucherAudit" />
                <span class="voucher-cell__meta">
                  {{ voucherCount(o) }} {{ voucherCount(o) === 1 ? 'comprobante' : 'comprobantes' }}
                  <template v-if="o.clientVouchers && o.clientVouchers.length"> · del inquilino</template>
                </span>
              </div>
              <span v-else class="dt-cell__secondary">—</span>
            </td>
            <td>
              <div class="dt-actions">
                <span class="dt-icon" title="Editar" @click="openEdit(o)">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                       stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                  </svg>
                </span>
                <span class="dt-icon" title="Registrar pago" @click="showPaySidebar = true; sidebarData = o">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" fill="none" stroke="currentColor"
                       stroke-width="32" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M416 221.25V416a48 48 0 01-48 48H144a48 48 0 01-48-48V96a48 48 0 0148-48h98.75a32 32 0 0122.62 9.37l141.26 141.26a32 32 0 019.37 22.62z"/>
                    <path d="M256 56v120a32 32 0 0032 32h120M176 288h160M176 368h160"/>
                  </svg>
                </span>
                <span class="dt-icon dt-icon--danger" title="Eliminar" @click="confirmDelete(o)">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                       stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <polyline points="3 6 5 6 21 6"/>
                    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                    <path d="M10 11v6"/><path d="M14 11v6"/>
                    <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"/>
                  </svg>
                </span>
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <EmptyState v-else title="Sin letras" message="No hay letras para mostrar con los filtros actuales."/>
    </div>

    <NewBill :client="client" @close="showNewSidebar = false" @add="addSidebar"
             :class="{'show': showNewSidebar}" title="Nueva letra" :show-navbar="showNewSidebar"/>
    <UpdateBill :data="sidebarData" @close="showUpdateSidebar = false" @update="updateSidebar"
                :class="{'show': showUpdateSidebar}" title="Editar letra" :show-navbar="showUpdateSidebar"/>
    <GenerateBills :client="client" @close="showGenerateSidebar = false" @add="addSidebar"
                   :class="{'show': showGenerateSidebar}" title="Generar letras" :show-navbar="showGenerateSidebar"/>
    <Pay :data="sidebarData" @close="showPaySidebar = false" @update="updateSidebar" @audit="updateSidebar"
         @added="addSidebar"
         :class="{'show': showPaySidebar}" :show-navbar="showPaySidebar"/>
    <SendNotification :show-navbar="showNotification" :data="client" @close="showNotification = false"/>
    <ConfirmDialog
      :show="showDeleteDialog"
      title="Eliminar letra"
      :message="deleteMsg"
      confirm-text="Eliminar"
      cancel-text="Cancelar"
      variant="danger"
      :loading="deleting"
      @confirm="doDelete"
      @cancel="showDeleteDialog = false"
    />
  </div>
</template>

<script>
import { db } from '@/firebase/firebaseConfig'
import NewBill from './NewBill'
import UpdateBill from './UpdateBill'
import GenerateBills from './Generate'
import Pay from './Pay'
import ConfirmDialog from '@/components/ConfirmDialog'
import HeroHeader from '@/components/HeroHeader'
import ChipsBar from '@/components/ChipsBar'
import EmptyState from '@/components/EmptyState'
import SortSelect from '@/components/SortSelect'
import VoucherStatus from '@/components/VoucherStatus'
import { sortItems } from '@/utils/sort'
import SendNotification from '../tenants/SendNotification.vue'
import moment from 'moment'

export default {
  name: 'Bills',
  props: { embedded: { type: Boolean, default: false } },
  components: { NewBill, UpdateBill, GenerateBills, Pay, ConfirmDialog, HeroHeader, ChipsBar, EmptyState, SortSelect, SendNotification, VoucherStatus },
  data () {
    return {
      list: [],
      client: { currency: {} },
      showNewSidebar: false,
      showUpdateSidebar: false,
      showGenerateSidebar: false,
      showPaySidebar: false,
      showNotification: false,
      sidebarData: {},
      filterPaid: null,
      sortBy: 'deadline_asc',
      sortOptions: [
        { value: 'deadline_asc', label: 'Vencimiento (próximo primero)', field: 'deadline', type: 'date', dir: 'asc' },
        { value: 'deadline_desc', label: 'Vencimiento (lejano primero)', field: 'deadline', type: 'date', dir: 'desc' },
        { value: 'amount_desc', label: 'Monto (mayor a menor)', field: 'total', type: 'number', dir: 'desc' },
        { value: 'status', label: 'Estado (pendientes primero)', field: 'paid', type: 'bool', dir: 'asc' }
      ],
      showDeleteDialog: false,
      billToDelete: null,
      deleting: false
    }
  },
  computed: {
    displayList () {
      return sortItems(this.list, this.sortOptions.find(o => o.value === this.sortBy))
    },
    deleteMsg () {
      if (!this.billToDelete) return ''
      return this.billToDelete.paid
        ? 'Esta letra ya está pagada. ¿Seguro que deseas eliminarla?'
        : '¿Eliminar esta letra? La acción no se puede deshacer.'
    }
  },
  async created () { await this.loadInfo() },
  methods: {
    // Cuántos comprobantes tiene la letra, contando los del admin y los del
    // inquilino sin repetir. Aquí había un enlace "Ver" que abría el PRIMERO y
    // sólo el primero: con varias imágenes adjuntas mentía sobre lo que había,
    // y quien lo apretaba se quedaba con la idea de haber visto el comprobante
    // cuando podía haber tres más. Se ven todos abriendo la letra.
    voucherCount (o) {
      const all = [
        ...(o.vouchers || []),
        ...(o.clientVouchers || []),
        ...(o.voucher ? [o.voucher] : [])
      ].filter(Boolean)
      return new Set(all).size
    },
    formatDate (d) { return moment(d).locale('es').format('DD/MMM/YY') },
    formatAmount (total, currency) {
      const code = currency && currency.code ? currency.code : 'PEN'
      return Intl.NumberFormat('en-US', { style: 'currency', currency: code }).format(total)
    },
    addSidebar (obj) { this.list.push(obj) },
    updateSidebar (o) {
      const idx = this.list.findIndex(d => d.id === o.id)
      if (idx !== -1) Object.assign(this.list[idx], o)
    },
    openEdit (o) {
      this.sidebarData = o
      this.showUpdateSidebar = true
    },
    confirmDelete (bill) {
      this.billToDelete = bill
      this.showDeleteDialog = true
    },
    async doDelete () {
      if (!this.billToDelete) return
      try {
        this.deleting = true
        await db.collection('clients').doc(this.client.id).collection('bills').doc(this.billToDelete.id).delete()
        this.list = this.list.filter(b => b.id !== this.billToDelete.id)
        this.$toast.success('Letra eliminada')
        this.showDeleteDialog = false
        this.billToDelete = null
      } catch (e) {
        this.$toast.error('Error al eliminar')
      } finally {
        this.deleting = false
      }
    },
    async loadInfo () {
      try {
        this.list = []
        this.$store.dispatch('showProgress')
        await this.getClient()
        await this.getBills()
      } catch (e) {
        this.$toast.error('Error al cargar datos')
      } finally {
        this.$store.dispatch('hideProgress')
      }
    },
    async getClient () {
      const doc = await db.collection('clients').doc(this.$route.params.id).get()
      if (doc.exists) this.client = { id: doc.id, ...doc.data() }
    },
    async getBills () {
      const ref = db.collection('clients').doc(this.client.id).collection('bills').orderBy('deadline', 'asc')
      const snap = this.filterPaid !== null ? await ref.where('paid', '==', this.filterPaid).get() : await ref.get()
      snap.forEach(doc => {
        const obj = { id: doc.id, ...doc.data() }
        obj.deadline = doc.data().deadline.toDate()
        obj.paymentDate = doc.data().paymentDate ? doc.data().paymentDate.toDate() : null
        this.list.push(obj)
      })
    },
    async goFilter () {
      try {
        this.list = []
        this.$store.dispatch('showProgress')
        await this.getBills()
      } catch (e) {
      } finally {
        this.$store.dispatch('hideProgress')
      }
    }
  }
}
</script>
